import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SessionService } from '../session.service';
import { UserService } from '../user/user.service';
import { Feature, WebsiteTextingStatus, WebsiteTextingSubscription, WebsiteTextingUsage } from './subscription';
import { getApiUrl } from '../helpers/rest-utils';
import { SmsCapabilitiesStatus } from '../agent/agent.service';

@Injectable({
  providedIn: 'root'
})
export class WebsiteTextingSubscriptionService {
  private subscriptionState = new BehaviorSubject<WebsiteTextingSubscription | null>(null);
  private usageState = new BehaviorSubject<WebsiteTextingUsage | null>(null);
  private smsStatusState = new BehaviorSubject<SmsCapabilitiesStatus | null>(null);

  subscription$ = this.subscriptionState.asObservable();
  usage$ = this.usageState.asObservable();
  smsStatus$ = this.smsStatusState.asObservable();

  constructor(
    private http: HttpClient,
    private session: SessionService,
    private user: UserService
  ) {
    this.session.terminate.subscribe(() => {
      this.subscriptionState.next(null);
      this.usageState.next(null);
      this.smsStatusState.next(null);
    });
  }

  private get smsEndpoint(): string {
    return `${getApiUrl()}businesses/${this.user.activeBusiness?.id}/phone-numbers/sms`;
  }

  /**
   * Derives website texting status from the main Subscription object.
   * Used as fallback if the dedicated API endpoint is not available.
   */
  private deriveStatusFromSubscription(): WebsiteTextingSubscription {
    const subscription = this.user.userData?.subscription;
    const status = subscription?.websiteTextingEnabled
      ? WebsiteTextingStatus.Enabled
      : WebsiteTextingStatus.Disabled;

    return {
      status,
      price: 50,
      conversationsIncluded: 25,
      overageRate: 1,
      nextBillingDate: status === WebsiteTextingStatus.Enabled
        ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        : null,
      cancellationDate: null,
      discountedPrice: null,
      couponsApplied: []
    };
  }

  get currentSubscription(): WebsiteTextingSubscription | null {
    return this.subscriptionState.value;
  }

  get currentUsage(): WebsiteTextingUsage | null {
    return this.usageState.value;
  }

  get currentSmsStatus(): SmsCapabilitiesStatus | null {
    return this.smsStatusState.value;
  }

  get isEnabled(): boolean {
    return this.currentSubscription?.status === WebsiteTextingStatus.Enabled;
  }

  get isSetToCancel(): boolean {
    return this.currentSubscription?.status === WebsiteTextingStatus.SetToCancel;
  }

  get isDisabled(): boolean {
    return !this.currentSubscription || this.currentSubscription?.status === WebsiteTextingStatus.Disabled;
  }

  get hasSmsConversationsFeature(): boolean {
    return this.user.userData?.subscription?.features?.includes(Feature.SmsConversations);
  }

  getStatus(): Observable<WebsiteTextingSubscription> {
    return this.http.get(this.smsEndpoint).pipe(
      tap((res: any) => {
        this.smsStatusState.next(this.normalizeSmsStatus(res?.data));
      }),
      map((res: any) => res?.websiteTexting || res?.data?.websiteTexting),
      tap(sub => this.subscriptionState.next(sub)),
      catchError(() => {
        const fallback = this.deriveStatusFromSubscription();
        this.subscriptionState.next(fallback);
        this.smsStatusState.next({ status: 'inactive' });
        return of(fallback);
      })
    );
  }

  private normalizeSmsStatus(data: any): SmsCapabilitiesStatus {
    if (!data) return { status: 'inactive' };

    const statusMap: { [key: string]: SmsCapabilitiesStatus['status'] } = {
      'inactive': 'inactive',
      'inProgress': 'in_progress',
      'in_progress': 'in_progress',
      'in-progress': 'in_progress',
      'active': 'active',
      'failed': 'failed'
    };

    const status = statusMap[data.status] || 'inactive';
    return { ...data, status };
  }

  subscribe(): Observable<any> {
    const body: any = {};
    return this.http.post(this.smsEndpoint, body).pipe(
      map((res: any) => res?.data || res),
      switchMap((data) => {
        // Shopify returns a confirmationUrl — pass it through without fetching status
        if (data?.confirmationUrl) {
          return of(data);
        }
        return this.getStatus();
      })
    );
  }

  cancel(reason: string, mode: 'end_of_period' | 'immediate' = 'end_of_period'): Observable<WebsiteTextingSubscription> {
    return this.http.post(`${getApiUrl()}subscription/website-texting/cancel`, { reason, mode }).pipe(
      map((res: any) => res?.data),
      switchMap((data) => {
        if (mode === 'immediate') {
          return this.getStatus();
        }
        this.subscriptionState.next(data);
        return of(data);
      })
    );
  }

  keep(): Observable<WebsiteTextingSubscription> {
    return this.http.post(`${getApiUrl()}subscription/website-texting/keep`, {}).pipe(
      switchMap(() => this.getStatus())
    );
  }

  reactivate(): Observable<any> {
    return this.http.post(this.smsEndpoint, {}).pipe(
      map((res: any) => res?.data || res),
      switchMap((data) => {
        // Shopify returns a confirmationUrl — pass it through without fetching status
        if (data?.confirmationUrl) {
          return of(data);
        }
        return this.getStatus();
      })
    );
  }

  getUsage(): Observable<WebsiteTextingUsage> {
    const usage = this.user.userData?.subscription?.websiteTextingUsage;
    if (usage) {
      this.usageState.next(usage);
      return of(usage);
    }
    return this.usageState.asObservable().pipe(map(u => u));
  }
}
